import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
    faArrowRightFromBracket,
    faArrowRightToBracket,
    faBook,
    faBoxesStacked,
    faCalendarCirclePlus,
    faChartSimple,
    faCircleQuestion,
    faClipboardList,
    faCommentsAlt,
    faDiamondExclamation,
    faFileMagnifyingGlass,
    faFolderOpen,
    faGear,
    faMessage,
    faPlugCircleBolt,
    faQuestionCircle,
    faTruck,
    faWarehouseFull,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery as useReactQuery } from '@tanstack/react-query';

import { useHubSpotChat } from '@spacefill/shared/src/hooks/useHubSpotChat';
import { call } from '@spacefill/shared/src/utils/Backend';
import { useFeatureTogglingContext } from '@spacefill/shared/src/utils/FeatureToggling';
import { getEnvironmentVariable } from '@spacefill/shared/src/utils/GetEnvVar';
import { useBrandPreviewContext } from '@spacefill/shared/src/utils/contexts/BrandContext';
import { useUserContext } from '@spacefill/shared/src/utils/contexts/UserContext';
import { Sidebar } from '@spacefill/uikit/src/components/Sidebar';

import { AdminCompanySwitcher } from './UserSwitcher';

const AppSidebar = () => {
    const { t } = useTranslation();
    const { user } = useUserContext();
    const location = useLocation();
    const attachmentsUrl = getEnvironmentVariable('attachmentsUrl');
    const { brandPreview } = useBrandPreviewContext();
    const { features } = useFeatureTogglingContext();

    const appBranding = {
        ...(user?.branding || {}),
        ...brandPreview,
    };

    const isCustomer = !!user.customerId;
    const isProvider = !!user.logisticProviderId;
    const isCarrier = !!user.carrierId;

    const shipperCanShowAppointments =
        isCustomer && user.logisticProviders.some((lsp) => lsp.settings?.appointmentScheduling?.enabled);

    const providerCanShowAppointments = isProvider && user.logisticProvider?.settings?.appointmentScheduling?.enabled;

    const { toggleHubSpotChat } = useHubSpotChat({ enable: getEnvironmentVariable('mode') !== 'development' });

    const { data: pdfConfigs, isLoading: isLoadingPdfConfigs } = useReactQuery({
        queryKey: ['email-to-order/pdf-configuration/getAll'],
        queryFn: () => call('email-to-order/pdf-configuration/getAll', {}, 'POST'),
        placeholderData: [],
        enabled: !isCustomer,
    });

    const shouldFetchCount = !!user.warehouseIds.length && !isLoadingPdfConfigs && pdfConfigs.length > 0;

    const { data: countObject } = useReactQuery({
        queryKey: ['unconfirmedorders/getAll'],
        queryFn: () => call('/unconfirmedorders/getAll', {}, 'POST'),
        placeholderData: {},
        enabled: shouldFetchCount,
    });

    const { data: unconfirmedTransports } = useReactQuery({
        queryKey: ['unconfirmed-transports/get-all'],
        queryFn: async () => {
            return await call('/unconfirmed-transports/get-all', {}, 'GET');
        },
        enabled: !!user.logisticProvider.id && user?.isTransportOrdersManagementEnabled,
        placeholderData: [],
    });

    return (
        <Fragment>
            <Sidebar
                staffDebugTools={user.isStaff && <AdminCompanySwitcher />}
                logoUrl={appBranding?.logoUrl}
                mainNavigation={[
                    {
                        title: t('Dashboards'),
                        icon: <FontAwesomeIcon icon={faChartSimple} />,
                        isActive: location.pathname.includes('/analytics/'),
                        to: '/analytics/',
                        dataTestid: 'nav-dashboards',
                        conditionForDisplay: () => !isCarrier,
                    },

                    // ORDERS
                    {
                        title: t('Entry_plural'),
                        icon: (
                            <FontAwesomeIcon
                                icon={faArrowRightToBracket}
                                className='rotate-180'
                            />
                        ),
                        isActive: !!location.pathname.match(
                            /\/logistic-management\/(warehouses\/[a-f0-9-]+\/)?entry-orders\/?/
                        ),
                        to: '/logistic-management/entry-orders/',
                        dataTestid: 'nav-entry-orders',
                        conditionForDisplay: () => user.warehouseIds.length > 0 && !isCarrier,
                        subItems: user.warehouses.length > 1 && [
                            {
                                title: t('All warehouses'),
                                isActive: location.pathname === '/logistic-management/entry-orders/',
                                icon: (
                                    <FontAwesomeIcon
                                        className='w-8'
                                        icon={faWarehouseFull}
                                    />
                                ),
                                hasRoundedIcon: true,
                                bottomDivider: true,
                                to: '/logistic-management/entry-orders/',
                            },
                            ...user.warehouses.map((warehouse) => ({
                                title: warehouse.label,
                                isActive: !!location.pathname.match(
                                    new RegExp(`/logistic-management/warehouses/${warehouse.id}/entry-orders/`)
                                ),
                                icon: (
                                    <FontAwesomeIcon
                                        className='w-8'
                                        icon={faWarehouseFull}
                                    />
                                ),
                                hasRoundedIcon: true,
                                logoUrl: warehouse?.logisticProvider?.logoUrl
                                    ? `${attachmentsUrl}/${warehouse?.logisticProvider?.logoUrl}`
                                    : '',
                                to: `/logistic-management/warehouses/${warehouse.id}/entry-orders/`,
                            })),
                        ],
                    },
                    {
                        title: t('Exit_plural'),
                        icon: <FontAwesomeIcon icon={faArrowRightFromBracket} />,
                        isActive: !!location.pathname.match(
                            /\/logistic-management\/(warehouses\/[a-f0-9-]+\/)?exit-orders\/?/
                        ),
                        to: '/logistic-management/exit-orders/',
                        dataTestid: 'nav-exit-orders',
                        conditionForDisplay: () => user.warehouseIds.length > 0 && !isCarrier,
                        subItems: user.warehouses.length > 1 && [
                            {
                                title: t('All warehouses'),
                                isActive: location.pathname === '/logistic-management/exit-orders/',
                                icon: (
                                    <FontAwesomeIcon
                                        className='w-8'
                                        icon={faWarehouseFull}
                                    />
                                ),
                                hasRoundedIcon: true,
                                bottomDivider: true,
                                to: '/logistic-management/exit-orders/',
                            },
                            ...user.warehouses.map((warehouse) => ({
                                title: warehouse.label,
                                isActive: !!location.pathname.match(
                                    new RegExp(`/logistic-management/warehouses/${warehouse.id}/exit-orders/`)
                                ),
                                icon: (
                                    <FontAwesomeIcon
                                        className='w-8'
                                        icon={faWarehouseFull}
                                    />
                                ),
                                hasRoundedIcon: true,
                                logoUrl: warehouse?.logisticProvider?.logoUrl
                                    ? `${attachmentsUrl}/${warehouse?.logisticProvider?.logoUrl}`
                                    : '',
                                to: `/logistic-management/warehouses/${warehouse.id}/exit-orders/`,
                            })),
                        ],
                    },

                    // APPOINTMENTS
                    {
                        title: t('Appointments'),
                        icon: <FontAwesomeIcon icon={faCalendarCirclePlus} />,
                        isActive: location.pathname.includes('/appointments/list/'),
                        to: '/appointments/list/',
                        dataTestid: 'nav-appointments',
                        conditionForDisplay: () => shipperCanShowAppointments,
                    },
                    {
                        title: t('Appointments'),
                        icon: <FontAwesomeIcon icon={faCalendarCirclePlus} />,
                        isActive: !!location.pathname.match(
                            /\/appointments\/(warehouses\/[a-f0-9-]+\/)?(calendar|list)\/?/
                        ),
                        to: '/appointments/calendar/',
                        dataTestid: 'nav-appointments',
                        conditionForDisplay: () => providerCanShowAppointments,
                        subItems: user.warehouses.length > 1 && [
                            {
                                title: t('All warehouses'),
                                isActive: location.pathname === '/appointments/calendar/',
                                icon: (
                                    <FontAwesomeIcon
                                        className='w-8'
                                        icon={faWarehouseFull}
                                    />
                                ),
                                hasRoundedIcon: true,
                                bottomDivider: true,
                                to: '/appointments/calendar/',
                            },
                            ...user.warehouses.map((warehouse) => ({
                                title: warehouse.label,
                                isActive: !!location.pathname.match(
                                    new RegExp(`/appointments/warehouses/${warehouse.id}/calendar/`)
                                ),
                                icon: (
                                    <FontAwesomeIcon
                                        className='w-8'
                                        icon={faWarehouseFull}
                                    />
                                ),
                                hasRoundedIcon: true,
                                logoUrl: warehouse?.logisticProvider?.logoUrl
                                    ? `${attachmentsUrl}/${warehouse?.logisticProvider?.logoUrl}`
                                    : '',
                                to: `/appointments/warehouses/${warehouse.id}/calendar/`,
                            })),
                        ],
                    },
                    // OTHERS
                    {
                        title: t('Stock situation'),
                        icon: <FontAwesomeIcon icon={faBoxesStacked} />,
                        isActive: !!location.pathname.match(
                            /\/logistic-management\/(warehouses\/[a-f0-9-]+\/)?stock-situation\/?/
                        ),
                        to: '/logistic-management/stock-situation/',
                        dataTestid: 'nav-stock',
                        conditionForDisplay: () => user.warehouseIds.length > 0 && !isCarrier,
                        subItems: user.warehouses.length > 1 && [
                            {
                                title: t('All warehouses'),
                                isActive: location.pathname === '/logistic-management/stock-situation/',
                                icon: (
                                    <FontAwesomeIcon
                                        className='w-8'
                                        icon={faWarehouseFull}
                                    />
                                ),
                                hasRoundedIcon: true,
                                bottomDivider: true,
                                to: '/logistic-management/stock-situation/',
                            },
                            ...user.warehouses.map((warehouse) => ({
                                title: warehouse.label,
                                isActive:
                                    location.pathname ===
                                    `/logistic-management/warehouses/${warehouse.id}/stock-situation/`,
                                icon: (
                                    <FontAwesomeIcon
                                        className='w-8'
                                        icon={faWarehouseFull}
                                    />
                                ),
                                hasRoundedIcon: true,
                                logoUrl: warehouse?.logisticProvider?.logoUrl
                                    ? `${attachmentsUrl}/${warehouse?.logisticProvider?.logoUrl}`
                                    : '',
                                to: `/logistic-management/warehouses/${warehouse.id}/stock-situation/`,
                            })),
                        ],
                    },
                    {
                        title: t('Transports'),
                        icon: <FontAwesomeIcon icon={faTruck} />,
                        isActive: location.pathname.includes('/transports/'),
                        to: '/transports/',
                        dataTestid: 'nav-transports-orders',
                        conditionForDisplay: () => user?.isTransportOrdersManagementEnabled || isCarrier,
                    },
                    {
                        title: t('References'),
                        icon: <FontAwesomeIcon icon={faBook} />,
                        isActive: location.pathname.includes('/logistic-management/inventory/items/'),
                        to: '/logistic-management/inventory/items/',
                        dataTestid: 'nav-ref',
                        conditionForDisplay: () => user.warehouseIds.length > 0 && !isCarrier,
                    },

                    {
                        title: t('Incidents'),
                        icon: <FontAwesomeIcon icon={faDiamondExclamation} />,
                        isActive: !!location.pathname.match(/\/logistic-management\/incidents\/?/),
                        to: '/logistic-management/incidents/',
                        dataTestid: 'nav-incidents',
                        conditionForDisplay: () =>
                            user.warehouseIds.length > 0 || user?.isTransportOrdersManagementEnabled || isCarrier,
                    },
                    {
                        title: t('Documents'),
                        icon: <FontAwesomeIcon icon={faFolderOpen} />,
                        isActive: location.pathname === '/documents/',
                        to: '/documents/',
                        dataTestid: 'nav-documents',
                        conditionForDisplay: () => user.warehouseIds.length > 0 || isCarrier,
                    },

                    {
                        title: t('Integrations'),
                        icon: <FontAwesomeIcon icon={faPlugCircleBolt} />,
                        isActive: location.pathname.startsWith('/logistic-management/integrations/'),
                        to: '/logistic-management/integrations/jobs',
                        dataTestid: 'nav-integrations',
                        conditionForDisplay: () => {
                            return (
                                (!!user?.logisticProvider?.settings?.integrationMonitoringEnabled ||
                                    !!user?.customer?.settings?.integrationMonitoringEnabled) &&
                                !isCarrier
                            );
                        },
                    },
                    {
                        title: t('Orders to review'),
                        icon: <FontAwesomeIcon icon={faFileMagnifyingGlass} />,
                        isActive: location.pathname.includes('/logistic-management/unconfirmed-orders/'),
                        to: '/logistic-management/unconfirmed-orders/',
                        countValue: countObject ? countObject[1] : undefined,
                        dataTestid: 'storage-emails',
                        conditionForDisplay: () => {
                            return pdfConfigs && pdfConfigs.length > 0 && isProvider;
                        },
                    },
                    {
                        title: t('Transports to review'),
                        icon: <FontAwesomeIcon icon={faFileMagnifyingGlass} />,
                        isActive: location.pathname.includes('/unconfirmed-transports/'),
                        to: '/unconfirmed-transports/',
                        countValue: unconfirmedTransports[1],
                        dataTestid: 'nav-unconfirmed-transports-orders',
                        conditionForDisplay: () =>
                            isProvider && unconfirmedTransports && user?.isTransportOrdersManagementEnabled,
                    },
                    {
                        title: t('Additional requests'),
                        icon: <FontAwesomeIcon icon={faClipboardList} />,
                        isActive: location.pathname === '/additional-requests/',
                        to: '/additional-requests/',
                        dataTestid: 'nav-additional-requests',
                        conditionForDisplay: () =>
                            features.additional_requests && user.features.canUseAdditionalRequests,
                    },
                ]}
                bottomNavigation={[
                    {
                        title: t('Assistant'),
                        icon: <FontAwesomeIcon icon={faMessage} />,
                        onClick: toggleHubSpotChat,
                        dataTestid: 'nav-chat',
                    },
                    {
                        title: t('Support'),
                        icon: <FontAwesomeIcon icon={faCircleQuestion} />,
                        subItems: [
                            {
                                title: t('Contact us'),
                                icon: <FontAwesomeIcon icon={faCommentsAlt} />,
                                href:
                                    user?.preferredLanguage === 'FR'
                                        ? `https://share.hsforms.com/1kZiXiThKTPyswL3sMULDkw4gsew?email=${
                                              user.email || ''
                                          }`
                                        : user?.preferredLanguage === 'DE'
                                          ? `https://share.hsforms.com/1LBgZ6T3IStyU8lNqtywI_g4gsew?email=${
                                                user.email || ''
                                            }`
                                          : `https://share.hsforms.com/10jZcnqXgR-W_9-CDlcusoA4gsew?email=${
                                                user.email || ''
                                            }`,
                                target: '_blank',
                            },
                            {
                                title: t('Help Center'),
                                icon: <FontAwesomeIcon icon={faQuestionCircle} />,
                                href:
                                    user?.preferredLanguage === 'FR'
                                        ? 'https://help.spacefill.eu/knowledge'
                                        : user?.preferredLanguage === 'DE'
                                          ? 'https://help.spacefill.eu/de/knowledge'
                                          : 'https://help.spacefill.eu/en/knowledge',
                                target: '_blank',
                            },
                        ],
                    },
                    {
                        title: t('Settings'),
                        icon: <FontAwesomeIcon icon={faGear} />,
                        dataTestid: 'nav-settings',
                        isActive: location.pathname.startsWith('/settings/'),
                        to: '/settings/users/',
                    },
                ]}
            />
        </Fragment>
    );
};

export { AppSidebar };
